import { DataTable } from '../DataTable/DataTable'
import React from 'react'
import merge from 'lodash/merge'
import { useFileBrowserContext } from './Context'
import type { ListFieldConfig, UseMutationProps } from '@ztechapps/common.hook'
import { ListFieldTypeEnum } from '@ztechapps/common.hook'
import type { File } from '@ztechapps/common.contract'
import { LocalDateTime } from '@ztechapps/webui.date'
import { formatConstants, formatNumber } from '@ztechapps/common.utils'
import { HeaderCell } from '../DataTable/table-header/HeaderCell'
import { BodyCell } from '../DataTable/table-body/BodyCell'
import { HorizontalStack } from '@ztechapps/webui.util'
import { BusyButtonLink, CopyToClipboardButton } from '@ztechapps/webui.button'
import { InlineInput, TagsInlineInput } from '@ztechapps/webui.input'
import { gql } from '@apollo/client'
import type { BodyRowConfig, HeaderRowConfig } from '../DataTable/types'
import { MenuHeaderCell } from '../DataTable/table-header/MenuHeaderCell'
import { RowNumberBodyCell } from '../DataTable/table-body/RowNumberBodyCell'

export const fieldConfig: ListFieldConfig = {
  name: { name: 'name', defaultOnSearch: true, size: 400, index: 0 },
  description: {
    name: 'description',
    size: 300,
    index: 1,
  },
  size: {
    name: 'size',
    index: 2,
    type: ListFieldTypeEnum.number,
    excludeOnSearch: true,
    size: 200,
  },
  directoryTree: {
    name: 'directoryTree',
    displayName: 'Path',
    excludeOnSort: true,
    size: 400,
    index: 3,
    type: ListFieldTypeEnum.array,
    isVisible: true,
  },
  updatedAt: {
    name: 'updatedAt',
    index: 4,
    type: ListFieldTypeEnum.dateTimeZone,
    size: 200,
    excludeOnSearch: true,
  },
  tags: { name: 'tags', excludeOnSort: true, size: 120, index: 5, type: ListFieldTypeEnum.array },

  createdAt: {
    name: 'createdAt',
    index: 6,
    type: ListFieldTypeEnum.dateTimeZone,
    isVisible: false,
    size: 200,
    excludeOnSearch: true,
  },
  key: {
    name: 'key',
    index: 7,
    isVisible: false,
  },
  mimetype: {
    name: 'mimetype',
    displayName: 'Type',
    index: 8,
    isVisible: false,
  },
}

export const FileList = () => {
  const { fileListResult, fields } = useFileBrowserContext()

  return (
    <DataTable
      name="files-list-v1"
      fields={merge({}, fieldConfig, fields)}
      listResult={fileListResult}
      row={ListRow}
      head={ListHeader}
      stats={'files'}
      height={'75vh'}
      tableOptions={{
        responsive: true,
      }}
    />
  )
}

export const ListHeader = (props: HeaderRowConfig<File>) => {
  return (
    <>
      <MenuHeaderCell />
      {props._renderDataHeaderCell()}
      <HeaderCell> </HeaderCell>
    </>
  )
}

export const ListRow = (props: BodyRowConfig<File>) => {
  const { createdAt, updatedAt, size, uri } = props
  return (
    <>
      <RowNumberBodyCell {...props} />

      {props._renderDataBodyCell({
        createdAt: <LocalDateTime value={createdAt} />,
        updatedAt: <LocalDateTime value={updatedAt} />,
        size: <>{formatNumber(size, formatConstants.bytes)}</>,
        directoryTree: <DirectoryTreeField {...props} />,
        description: <DescriptionField {...props} />,
        tags: <TagsField {...props} />,
      })}

      <BodyCell>
        <HorizontalStack gap={2}>
          <BusyButtonLink
            icon="external-link-alt"
            title="Open file"
            onClick={() => {
              window.open(uri, '_blank')
            }}
          />

          <CopyToClipboardButton textToCopy={uri} />
        </HorizontalStack>
      </BodyCell>
    </>
  )
}

const mutationQuery = gql`
  mutation updateFile($filter: JSON, $input: JSON!) {
    updateFile(filter: $filter, input: $input) {
      _id
      description
      tags
    }
  }
`

const mutationConfig: UseMutationProps = {
  query: mutationQuery,
}

export const DescriptionField = (props: BodyRowConfig<File>) => {
  return <InlineInput mutationConfig={mutationConfig} name={'description'} initialValues={props} />
}

export const TagsField = (props: BodyRowConfig<File>) => {
  return <TagsInlineInput mutationConfig={mutationConfig} name={'tags'} initialValues={props} />
}

const DirectoryTreeField = (props: BodyRowConfig<File>) => {
  const { directoryTree = [] } = props
  const normalizedTree = directoryTree ?? []
  const result = normalizedTree.length > 1 ? normalizedTree.slice(0, -1).join(' / ') : '' // return "" if empty or only one element

  return result
}
